import {makeAutoObservable} from 'mobx';
import {fieldOptions} from './ApprovalXref';
import {Parameter} from './Parameter';
import Utils from '../../Utils';

class ApprovalParameter extends Parameter {
    valueAsString = () => {
        return fieldOptions.hasOwnProperty(this.id)
            ? (fieldOptions[this.id as keyof typeof fieldOptions].options
                  .find((o) => Utils.MakeVarName(o) === this.value)
                  ?.toString() ?? this.value.toString())
            : this.value.toString();
    };
}

export class Approval {
    /* Pre-Approval Fields */
    agentName: Parameter = new ApprovalParameter('agentName');
    bdsName: Parameter = new ApprovalParameter('bdsName');
    agentProducts: Parameter = new ApprovalParameter('agentProducts', ['FGEverlast']);
    prosperNotChosenReason: Parameter = new ApprovalParameter('prosperNotChosenReason');
    prosperNotChosenDetails: Parameter = new ApprovalParameter('prosperNotChosenDetails');
    fgEverlastNotChosenReason: Parameter = new ApprovalParameter('fgEverlastNotChosenReason');
    firstName: Parameter = new ApprovalParameter('firstName');
    lastName: Parameter = new ApprovalParameter('lastName');
    middleName: Parameter = new ApprovalParameter('middleName');
    suffix: Parameter = new ApprovalParameter('suffix');
    phoneNumber: Parameter = new ApprovalParameter('phoneNumber');
    bestTimeToCall: Parameter = new ApprovalParameter('bestTimeToCall');
    emailAddress: Parameter = new ApprovalParameter('emailAddress');
    bmonth: Parameter = new ApprovalParameter('bmonth');
    bday: Parameter = new ApprovalParameter('bday');
    byear: Parameter = new ApprovalParameter('byear');
    age: Parameter = new ApprovalParameter('age');
    gender: Parameter = new ApprovalParameter('gender');
    maritalStatus: Parameter = new ApprovalParameter('maritalStatus');
    address: Parameter = new ApprovalParameter('address');
    addressCity: Parameter = new ApprovalParameter('addressCity');
    state: Parameter = new ApprovalParameter('state');
    addressZipCode: Parameter = new ApprovalParameter('addressZipCode');
    height: Parameter = new ApprovalParameter('height');
    weight: Parameter = new ApprovalParameter('weight');
    weightChanged: Parameter = new ApprovalParameter('weightChanged');
    sameOwner: Parameter = new ApprovalParameter('sameOwner');
    policyOwnerName: Parameter = new ApprovalParameter('policyOwnerName');
    ownerSocialSecurityNumber: Parameter = new ApprovalParameter('ownerSocialSecurityNumber');
    ownerBMonth: Parameter = new ApprovalParameter('ownerBMonth');
    ownerBday: Parameter = new ApprovalParameter('ownerBday');
    ownerByear: Parameter = new ApprovalParameter('ownerByear');
    ownerIssueState: Parameter = new ApprovalParameter('ownerIssueState');
    ownerNumber: Parameter = new ApprovalParameter('ownerNumber');
    ownerExpiration: Parameter = new ApprovalParameter('ownerExpiration');
    amountOfCoverageMomHas: Parameter = new ApprovalParameter('amountOfCoverageMomHas');
    amountOfCoverageDadHas: Parameter = new ApprovalParameter('amountOfCoverageDadHas');
    ownerCitizenshipCountry: Parameter = new ApprovalParameter('ownerCitizenshipCountry');
    relationshipToInsured: Parameter = new ApprovalParameter('relationshipToInsured');
    policyOwnerPhone: Parameter = new ApprovalParameter('policyOwnerPhone');
    policyOwnerEmail: Parameter = new ApprovalParameter('policyOwnerEmail');
    policyOwnerStreetAddress: Parameter = new ApprovalParameter('policyOwnerStreetAddress');
    policyOwnerState: Parameter = new ApprovalParameter('policyOwnerState');
    policyOwnerCity: Parameter = new ApprovalParameter('policyOwnerCity');
    policyOwnerZipcode: Parameter = new ApprovalParameter('policyOwnerZipcode');
    incomeIndividual: Parameter = new ApprovalParameter('incomeIndividual');
    incomeHouseholdText: Parameter = new ApprovalParameter('incomeHouseholdText');
    currentlyEmployed: Parameter = new ApprovalParameter('currentlyEmployed');
    currentSelfEmployed: Parameter = new ApprovalParameter('currentSelfEmployed');
    nameOfEmployer: Parameter = new ApprovalParameter('nameOfEmployer');
    timeEmployed: Parameter = new ApprovalParameter('timeEmployed');
    employmentDays: Parameter = new ApprovalParameter('employmentDays');
    employerAddress: Parameter = new ApprovalParameter('employerAddress');
    occupation: Parameter = new ApprovalParameter('occupation');
    industryDuties: Parameter = new ApprovalParameter('industryDuties');
    prevSelfEmployed: Parameter = new ApprovalParameter('prevSelfEmployed');
    prevEmployName: Parameter = new ApprovalParameter('prevEmployName');
    prevEmployStart: Parameter = new ApprovalParameter('prevEmployStart');
    prevEmployOccup: Parameter = new ApprovalParameter('prevEmployOccup');
    prevEmployIndustry: Parameter = new ApprovalParameter('prevEmployIndustry');
    reasonUnemployed: Parameter = new ApprovalParameter('reasonUnemployed');
    previousJobInfo: Parameter = new ApprovalParameter('previousJobInfo');
    previousIncome: Parameter = new ApprovalParameter('previousIncome');
    retiredInfo: Parameter = new ApprovalParameter('retiredInfo');
    retiredIncome: Parameter = new ApprovalParameter('retiredIncome');
    schoolInfo: Parameter = new ApprovalParameter('schoolInfo');
    disabilityInfo: Parameter = new ApprovalParameter('disabilityInfo');
    otherInfo: Parameter = new ApprovalParameter('otherInfo');
    estimatedSavingsTotal: Parameter = new ApprovalParameter('estimatedSavingsTotal');
    estimatedDebtTotal: Parameter = new ApprovalParameter('estimatedDebtTotal');
    birthCountry: Parameter = new ApprovalParameter('birthCountry');
    birthState: Parameter = new ApprovalParameter('birthState');
    birthCity: Parameter = new ApprovalParameter('birthCity');
    birthAddressZipCode: Parameter = new ApprovalParameter('birthAddressZipCode');
    usCitizenship: Parameter = Object.assign(new ApprovalParameter('usCitizenship'), {name: 'usCitizenship'});
    usPermResNumber: Parameter = new ApprovalParameter('usPermResNumber');
    usVisaType: Parameter = new ApprovalParameter('usVisaType');
    usVisaNumber: Parameter = new ApprovalParameter('usVisaNumber');
    usTinNumber: Parameter = new ApprovalParameter('usTinNumber');
    validDriversLicense: Parameter = new ApprovalParameter('validDriversLicense');
    ndiReason: Parameter = new ApprovalParameter('ndiReason');
    driversLicenseState: Parameter = new ApprovalParameter('driversLicenseState');
    driversLicenseNumber: Parameter = new ApprovalParameter('driversLicenseNumber');
    driversLicExpiration: Parameter = new ApprovalParameter('driversLicExpiration');
    stateIdState: Parameter = new ApprovalParameter('stateIdState');
    stateIdNumber: Parameter = new ApprovalParameter('stateIdNumber');
    stateIdExpiration: Parameter = new ApprovalParameter('stateIdExpiration');
    noDriversLicenseReason: Parameter = new ApprovalParameter('noDriversLicenseReason');
    modeOfTransport: Parameter = new ApprovalParameter('modeOfTransport');
    military: Parameter = new ApprovalParameter('military');
    militaryServiceBranch: Parameter = new ApprovalParameter('militaryServiceBranch');
    militaryServieStatus: Parameter = new ApprovalParameter('militaryServieStatus');
    militaryServePeriod: Parameter = new ApprovalParameter('militaryServePeriod');
    numberOfBenes: Parameter = new ApprovalParameter('numberOfBenes');
    beneType1: Parameter = new ApprovalParameter('beneType1');
    beneInfo1: Parameter = new ApprovalParameter('beneInfo1');
    benePercent1: Parameter = new ApprovalParameter('benePercent1');
    beneRelationship1: Parameter = new ApprovalParameter('beneRelationship1');
    beneRelationshipAgenium1: Parameter = new ApprovalParameter('beneRelationshipAgenium1');
    beneMonth1: Parameter = new ApprovalParameter('beneMonth1');
    beneDay1: Parameter = new ApprovalParameter('beneDay1');
    beneYear1: Parameter = new ApprovalParameter('beneYear1');
    beneType2: Parameter = new ApprovalParameter('beneType2');
    beneInfo2: Parameter = new ApprovalParameter('beneInfo2');
    benePercent2: Parameter = new ApprovalParameter('benePercent2');
    beneRelationship2: Parameter = new ApprovalParameter('beneRelationship2');
    beneRelationshipAgenium2: Parameter = new ApprovalParameter('beneRelationshipAgenium2');
    beneMonth2: Parameter = new ApprovalParameter('beneMonth2');
    beneDay2: Parameter = new ApprovalParameter('beneDay2');
    beneYear2: Parameter = new ApprovalParameter('beneYear2');
    beneType3: Parameter = new ApprovalParameter('beneType3');
    beneInfo3: Parameter = new ApprovalParameter('beneInfo3');
    benePercent3: Parameter = new ApprovalParameter('benePercent3');
    beneRelationship3: Parameter = new ApprovalParameter('beneRelationship3');
    beneRelationshipAgenium3: Parameter = new ApprovalParameter('beneRelationshipAgenium3');
    beneMonth3: Parameter = new ApprovalParameter('beneMonth3');
    beneDay3: Parameter = new ApprovalParameter('beneDay3');
    beneYear3: Parameter = new ApprovalParameter('beneYear3');
    beneType4: Parameter = new ApprovalParameter('beneType4');
    beneInfo4: Parameter = new ApprovalParameter('beneInfo4');
    benePercent4: Parameter = new ApprovalParameter('benePercent4');
    beneRelationship4: Parameter = new ApprovalParameter('beneRelationship4');
    beneRelationshipAgenium4: Parameter = new ApprovalParameter('beneRelationshipAgenium4');
    beneMonth4: Parameter = new ApprovalParameter('beneMonth4');
    beneDay4: Parameter = new ApprovalParameter('beneDay4');
    beneYear4: Parameter = new ApprovalParameter('beneYear4');
    beneType5: Parameter = new ApprovalParameter('beneType5');
    beneInfo5: Parameter = new ApprovalParameter('beneInfo5');
    benePercent5: Parameter = new ApprovalParameter('benePercent5');
    beneRelationship5: Parameter = new ApprovalParameter('beneRelationship5');
    beneRelationshipAgenium5: Parameter = new ApprovalParameter('beneRelationshipAgenium5');
    beneMonth5: Parameter = new ApprovalParameter('beneMonth5');
    beneDay5: Parameter = new ApprovalParameter('beneDay5');
    beneYear5: Parameter = new ApprovalParameter('beneYear5');
    beneType6: Parameter = new ApprovalParameter('beneType6');
    beneInfo6: Parameter = new ApprovalParameter('beneInfo6');
    benePercent6: Parameter = new ApprovalParameter('benePercent6');
    beneRelationship6: Parameter = new ApprovalParameter('beneRelationship6');
    beneRelationshipAgenium6: Parameter = new ApprovalParameter('beneRelationshipAgenium6');
    beneMonth6: Parameter = new ApprovalParameter('beneMonth6');
    beneDay6: Parameter = new ApprovalParameter('beneDay6');
    beneYear6: Parameter = new ApprovalParameter('beneYear6');
    beneTotalPercent: Parameter = new ApprovalParameter('beneTotalPercent');
    contBene: Parameter = new ApprovalParameter('contBene');
    contNumberOfBenes: Parameter = new ApprovalParameter('contNumberOfBenes');
    contBeneType1: Parameter = new ApprovalParameter('contBeneType1');
    contBeneInfo1: Parameter = new ApprovalParameter('contBeneInfo1');
    contBenePercent1: Parameter = new ApprovalParameter('contBenePercent1');
    contBeneRelationship1: Parameter = new ApprovalParameter('contBeneRelationship1');
    contBeneRelationshipAgenium1: Parameter = new ApprovalParameter('contBeneRelationshipAgenium1');
    contBeneMonth1: Parameter = new ApprovalParameter('contBeneMonth1');
    contBday1: Parameter = new ApprovalParameter('contBday1');
    contBeneYear1: Parameter = new ApprovalParameter('contBeneYear1');
    contBeneType2: Parameter = new ApprovalParameter('contBeneType2');
    contBeneInfo2: Parameter = new ApprovalParameter('contBeneInfo2');
    contBenePercent2: Parameter = new ApprovalParameter('contBenePercent2');
    contBeneRelationship2: Parameter = new ApprovalParameter('contBeneRelationship2');
    contBeneRelationshipAgenium2: Parameter = new ApprovalParameter('contBeneRelationshipAgenium2');
    contBeneMonth2: Parameter = new ApprovalParameter('contBeneMonth2');
    contBday2: Parameter = new ApprovalParameter('contBday2');
    contBeneYear2: Parameter = new ApprovalParameter('contBeneYear2');
    contBeneType3: Parameter = new ApprovalParameter('contBeneType3');
    contBeneInfo3: Parameter = new ApprovalParameter('contBeneInfo3');
    contBenePercent3: Parameter = new ApprovalParameter('contBenePercent3');
    contBeneRelationship3: Parameter = new ApprovalParameter('contBeneRelationship3');
    contBeneRelationshipAgenium3: Parameter = new ApprovalParameter('contBeneRelationshipAgenium3');
    contBeneMonth3: Parameter = new ApprovalParameter('contBeneMonth3');
    contBday3: Parameter = new ApprovalParameter('contBday3');
    contBeneYear3: Parameter = new ApprovalParameter('contBeneYear3');
    contBeneType4: Parameter = new ApprovalParameter('contBeneType4');
    contBeneInfo4: Parameter = new ApprovalParameter('contBeneInfo4');
    contBenePercent4: Parameter = new ApprovalParameter('contBenePercent4');
    contBeneRelationship4: Parameter = new ApprovalParameter('contBeneRelationship4');
    contBeneRelationshipAgenium4: Parameter = new ApprovalParameter('contBeneRelationshipAgenium4');
    contBeneMonth4: Parameter = new ApprovalParameter('contBeneMonth4');
    contBday4: Parameter = new ApprovalParameter('contBday4');
    contBeneYear4: Parameter = new ApprovalParameter('contBeneYear4');
    contBeneType5: Parameter = new ApprovalParameter('contBeneType5');
    contBeneInfo5: Parameter = new ApprovalParameter('contBeneInfo5');
    contBenePercent5: Parameter = new ApprovalParameter('contBenePercent5');
    contBeneRelationship5: Parameter = new ApprovalParameter('contBeneRelationship5');
    contBeneRelationshipAgenium5: Parameter = new ApprovalParameter('contBeneRelationshipAgenium5');
    contBeneMonth5: Parameter = new ApprovalParameter('contBeneMonth5');
    contBday5: Parameter = new ApprovalParameter('contBday5');
    contBeneYear5: Parameter = new ApprovalParameter('contBeneYear5');
    contBeneType6: Parameter = new ApprovalParameter('contBeneType6');
    contBeneInfo6: Parameter = new ApprovalParameter('contBeneInfo6');
    contBenePercent6: Parameter = new ApprovalParameter('contBenePercent6');
    contBeneRelationship6: Parameter = new ApprovalParameter('contBeneRelationship6');
    contBeneRelationshipAgenium6: Parameter = new ApprovalParameter('contBeneRelationshipAgenium6');
    contBeneMonth6: Parameter = new ApprovalParameter('contBeneMonth6');
    contBday6: Parameter = new ApprovalParameter('contBday6');
    contBeneYear6: Parameter = new ApprovalParameter('contBeneYear6');
    contBeneTotalPercent: Parameter = new ApprovalParameter('contBeneTotalPercent');
    existingCoverage: Parameter = new ApprovalParameter('existingCoverage');
    existingCarrier: Parameter = new ApprovalParameter('existingCarrier');
    existingCarrier1: Parameter = new ApprovalParameter('existingCarrier1');
    existingCarrier2: Parameter = new ApprovalParameter('existingCarrier2');
    existingPolicyType: Parameter = new ApprovalParameter('existingPolicyType');
    existingPolicyType1: Parameter = new ApprovalParameter('existingPolicyType1');
    existingPolicyType2: Parameter = new ApprovalParameter('existingPolicyType2');
    existingCoverageAmount: Parameter = new ApprovalParameter('existingCoverageAmount');
    existingCoverageAmount1: Parameter = new ApprovalParameter('existingCoverageAmount1');
    existingCoverageAmount2: Parameter = new ApprovalParameter('existingCoverageAmount2');
    policyNumber: Parameter = new ApprovalParameter('policyNumber');
    accidentalDeathBenefitAmount: Parameter = new ApprovalParameter('accidentalDeathBenefitAmount');
    howLongInforce: Parameter = new ApprovalParameter('howLongInforce');
    replacingExistingPolicy: Parameter = new ApprovalParameter('replacingExistingPolicy');
    replacingExistingPolicy1: Parameter = new ApprovalParameter('replacingExistingPolicy1');
    replacingExistingPolicy2: Parameter = new ApprovalParameter('replacingExistingPolicy2');
    shoppedCoverage: Parameter = new ApprovalParameter('shoppedCoverage');
    shoppedCarrier: Parameter = new ApprovalParameter('shoppedCarrier');
    shoppedPolicyType: Parameter = new ApprovalParameter('shoppedPolicyType');
    shoppedCoverageAmount: Parameter = new ApprovalParameter('shoppedCoverageAmount');
    policyEverBeenDeclined: Parameter = new ApprovalParameter('policyEverBeenDeclined');
    additionalConditions: Parameter = new ApprovalParameter('additionalConditions', 'NA');
    medicationHistory: Parameter = new ApprovalParameter('medicationHistory', 'NA');
    DrugorAlcoholDependenceWithdrawal: Parameter = new ApprovalParameter('DrugorAlcoholDependenceWithdrawal');
    alcoholDiagnosisDate: Parameter = new ApprovalParameter('alcoholDiagnosisDate');
    alcoholTreatment: Parameter = new ApprovalParameter('alcoholTreatment');
    alcoholTreatmentDetails: Parameter = new ApprovalParameter('alcoholTreatmentDetails');
    alcoholTreatmentDate: Parameter = new ApprovalParameter('alcoholTreatmentDate');
    alcoholTreatmentClinic: Parameter = new ApprovalParameter('alcoholTreatmentClinic');
    alcoholDrugFree: Parameter = new ApprovalParameter('alcoholDrugFree');
    alcoholBeverages: Parameter = new ApprovalParameter('alcoholBeverages');
    alcoholDrinkType: Parameter = new ApprovalParameter('alcoholDrinkType');
    weeklyAlcoholicDrinks: Parameter = new ApprovalParameter('weeklyAlcoholicDrinks');
    alcoholDrinks: Parameter = new ApprovalParameter('alcoholDrinks');
    alcoholMember: Parameter = new ApprovalParameter('alcoholMember');
    DrugOrAlcoholTreatmentDetailsNotes: Parameter = new ApprovalParameter('DrugOrAlcoholTreatmentDetailsNotes');
    typeOfDrug: Parameter = new ApprovalParameter('typeOfDrug');
    takingMedicineDueToDrug: Parameter = new ApprovalParameter('takingMedicineDueToDrug');
    medicineNameDueToDrug: Parameter = new ApprovalParameter('medicineNameDueToDrug');
    DUI: Parameter = new ApprovalParameter('DUI');
    DUIDate: Parameter = new ApprovalParameter('DUIDate');
    duiDate: Parameter = new ApprovalParameter('duiDate');
    duiNotes: Parameter = new ApprovalParameter('duiNotes');
    License: Parameter = new ApprovalParameter('License');
    suspendedLicenseDate: Parameter = new ApprovalParameter('suspendedLicenseDate');
    suspendedLicense: Parameter = new ApprovalParameter('suspendedLicense');
    licenseNotes: Parameter = new ApprovalParameter('licenseNotes');
    Movingviolations: Parameter = new ApprovalParameter('Movingviolations');
    violationNumbers: Parameter = new ApprovalParameter('violationNumbers');
    movingViolationsNotes: Parameter = new ApprovalParameter('movingViolationsNotes');
    Criminaloffense: Parameter = new ApprovalParameter('Criminaloffense');
    criminalOffenseChargeType: Parameter = new ApprovalParameter('criminalOffenseChargeType');
    criminalOffenseType: Parameter = new ApprovalParameter('criminalOffenseType');
    criminalOffenseIncarcerated: Parameter = new ApprovalParameter('criminalOffenseIncarcerated');
    criminalOffenseIncarcerationYear: Parameter = new ApprovalParameter('criminalOffenseIncarcerationYear');
    criminalOffenseIncarcerationYearOut: Parameter = new ApprovalParameter('criminalOffenseIncarcerationYearOut');
    criminalOffenseProbation: Parameter = new ApprovalParameter('criminalOffenseProbation');
    criminalOffenseDetails: Parameter = new ApprovalParameter('criminalOffenseDetails');
    criminalOffenseDate: Parameter = new ApprovalParameter('criminalOffenseDate');
    criminalParole: Parameter = new ApprovalParameter('criminalParole');
    criminalOffenseNotes: Parameter = new ApprovalParameter('criminalOffenseNotes');
    Bankruptcy: Parameter = new ApprovalParameter('Bankruptcy');
    HIV: Parameter = new ApprovalParameter('HIV');
    HIVDetail: Parameter = new ApprovalParameter('HIVDetail');
    extremeSports: Parameter = new ApprovalParameter('extremeSports');
    livingAssistance: Parameter = new ApprovalParameter('livingAssistance');
    bankruptcyYear: Parameter = new ApprovalParameter('bankruptcyYear');
    bankruptcyChapter: Parameter = new ApprovalParameter('bankruptcyChapter');
    bankruptcyOpen: Parameter = new ApprovalParameter('bankruptcyOpen');
    bankruptcyDischargeDate: Parameter = new ApprovalParameter('bankruptcyDischargeDate');
    bankruptcyNotes: Parameter = new ApprovalParameter('bankruptcyNotes');
    bankruptcyMultiple: Parameter = new ApprovalParameter('bankruptcyMultiple');
    bankruptcyMultipleDetail: Parameter = new ApprovalParameter('bankruptcyMultipleDetail');
    extremeSportsOptions: Parameter = new ApprovalParameter('extremeSportsOptions');
    extremeSportsScubaType: Parameter = new ApprovalParameter('extremeSportsScubaType');
    extremeSportsScubaDepth: Parameter = new ApprovalParameter('extremeSportsScubaDepth');
    TobaccoRating: Parameter = new ApprovalParameter('TobaccoRating');
    MarijuanaRating: Parameter = new ApprovalParameter('MarijuanaRating');
    OtherRating: Parameter = new ApprovalParameter('OtherRating');
    tobaccoProducts: Parameter = new ApprovalParameter('tobaccoProducts');
    tobaccoFrequency: Parameter = new ApprovalParameter('tobaccoFrequency');
    tobaccoLastUse: Parameter = new ApprovalParameter('tobaccoLastUse');
    marijuanaProducts: Parameter = new ApprovalParameter('marijuanaProducts');
    marijuanaFrequency: Parameter = new ApprovalParameter('marijuanaFrequency');
    marijuanaLastUse: Parameter = new ApprovalParameter('marijuanaLastUse');
    otherSubstance: Parameter = new ApprovalParameter('otherSubstance');
    otherProducts: Parameter = new ApprovalParameter('otherProducts');
    otherFrequency: Parameter = new ApprovalParameter('otherFrequency');
    otherLastUse: Parameter = new ApprovalParameter('otherLastUse');
    fatherLiving: Parameter = new ApprovalParameter('fatherLiving');
    fatherAge: Parameter = new ApprovalParameter('fatherAge');
    fatherPassingAge: Parameter = new ApprovalParameter('fatherPassingAge');
    fatherPassingCause: Parameter = new ApprovalParameter('fatherPassingCause');
    fatherUnknown: Parameter = new ApprovalParameter('fatherUnknown');
    motherLiving: Parameter = new ApprovalParameter('motherLiving');
    motherAge: Parameter = new ApprovalParameter('motherAge');
    motherPassingAge: Parameter = new ApprovalParameter('motherPassingAge');
    motherPassingCause: Parameter = new ApprovalParameter('motherPassingCause');
    motherUnknown: Parameter = new ApprovalParameter('motherUnknown');
    biologicalFamilyCondition: Parameter = new ApprovalParameter('biologicalFamilyCondition');
    biologicalFamilyConditionWhichMember: Parameter = new ApprovalParameter('biologicalFamilyConditionWhichMember');
    biologicalFamilyConditionFather: Parameter = new ApprovalParameter('biologicalFamilyConditionFather');
    biologicalFamilyConditionFatherCancerDiagnosedAge: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionFatherCancerDiagnosedAge',
    );
    biologicalFamilyConditionFatherCancerDied: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionFatherCancerDied',
    );
    biologicalFamilyConditionFatherCancerDiedAge: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionFatherCancerDiedAge',
    );
    biologicalFamilyConditionFatherCancerType: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionFatherCancerType',
    );
    biologicalFamilyConditionFatherHeartDiseaseAge: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionFatherHeartDiseaseAge',
    );
    biologicalFamilyConditionFatherHeartDiseaseDied: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionFatherHeartDiseaseDied',
    );
    biologicalFamilyConditionFatherHeartDiseaseDiedAgee: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionFatherHeartDiseaseDiedAgee',
    );
    biologicalFamilyConditionFatherDiabetesAge: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionFatherDiabetesAge',
    );
    biologicalFamilyConditionFatherDiabetesDied: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionFatherDiabetesDied',
    );
    biologicalFamilyConditionFatherDiabetesDiedAgee: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionFatherDiabetesDiedAgee',
    );
    biologicalFamilyConditionFatherStrokeAge: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionFatherStrokeAge',
    );
    biologicalFamilyConditionFatherStrokeDied: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionFatherStrokeDied',
    );
    biologicalFamilyConditionFatherStrokeDiedAgee: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionFatherStrokeDiedAgee',
    );
    biologicalFamilyConditionMother: Parameter = new ApprovalParameter('biologicalFamilyConditionMother');
    biologicalFamilyConditionMotherCancerDiagnosedAge: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionMotherCancerDiagnosedAge',
    );
    biologicalFamilyConditionMotherCancerDied: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionMotherCancerDied',
    );
    biologicalFamilyConditionMotherCancerDiedAge: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionMotherCancerDiedAge',
    );
    biologicalFamilyConditionMotherCancerType: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionMotherCancerType',
    );
    biologicalFamilyConditionMotherHeartDiseaseAge: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionMotherHeartDiseaseAge',
    );
    biologicalFamilyConditionMotherHeartDiseaseDied: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionMotherHeartDiseaseDied',
    );
    biologicalFamilyConditionMotherHeartDiseaseDiedAgee: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionMotherHeartDiseaseDiedAgee',
    );
    biologicalFamilyConditionMotherDiabetesAge: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionMotherDiabetesAge',
    );
    biologicalFamilyConditionMotherDiabetesDied: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionMotherDiabetesDied',
    );
    biologicalFamilyConditionMotherDiabetesDiedAgee: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionMotherDiabetesDiedAgee',
    );
    biologicalFamilyConditionMotherStrokeAge: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionMotherStrokeAge',
    );
    biologicalFamilyConditionMotherStrokeDied: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionMotherStrokeDied',
    );
    biologicalFamilyConditionMotherStrokeDiedAgee: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionMotherStrokeDiedAgee',
    );
    biologicalFamilyConditionBrother: Parameter = new ApprovalParameter('biologicalFamilyConditionBrother');
    biologicalFamilyConditionBrotherCancerDiagnosedAge: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionBrotherCancerDiagnosedAge',
    );
    biologicalFamilyConditionBrotherCancerDied: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionBrotherCancerDied',
    );
    biologicalFamilyConditionBrotherCancerDiedAge: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionBrotherCancerDiedAge',
    );
    biologicalFamilyConditionBrotherCancerType: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionBrotherCancerType',
    );
    biologicalFamilyConditionBrotherHeartDiseaseAge: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionBrotherHeartDiseaseAge',
    );
    biologicalFamilyConditionBrotherHeartDiseaseDied: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionBrotherHeartDiseaseDied',
    );
    biologicalFamilyConditionBrotherHeartDiseaseDiedAgee: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionBrotherHeartDiseaseDiedAgee',
    );
    biologicalFamilyConditionBrotherDiabetesAge: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionBrotherDiabetesAge',
    );
    biologicalFamilyConditionBrotherDiabetesDied: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionBrotherDiabetesDied',
    );
    biologicalFamilyConditionBrotherDiabetesDiedAgee: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionBrotherDiabetesDiedAgee',
    );
    biologicalFamilyConditionBrotherStrokeAge: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionBrotherStrokeAge',
    );
    biologicalFamilyConditionBrotherStrokeDied: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionBrotherStrokeDied',
    );
    biologicalFamilyConditionBrotherStrokeDiedAgee: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionBrotherStrokeDiedAgee',
    );
    biologicalFamilyConditionSister: Parameter = new ApprovalParameter('biologicalFamilyConditionSister');
    biologicalFamilyConditionSisterCancerDiagnosedAge: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionSisterCancerDiagnosedAge',
    );
    biologicalFamilyConditionSisterCancerDied: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionSisterCancerDied',
    );
    biologicalFamilyConditionSisterCancerDiedAge: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionSisterCancerDiedAge',
    );
    biologicalFamilyConditionSisterCancerType: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionSisterCancerType',
    );
    biologicalFamilyConditionSisterHeartDiseaseAge: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionSisterHeartDiseaseAge',
    );
    biologicalFamilyConditionSisterHeartDiseaseDied: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionSisterHeartDiseaseDied',
    );
    biologicalFamilyConditionSisterHeartDiseaseDiedAgee: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionSisterHeartDiseaseDiedAgee',
    );
    biologicalFamilyConditionSisterDiabetesAge: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionSisterDiabetesAge',
    );
    biologicalFamilyConditionSisterDiabetesDied: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionSisterDiabetesDied',
    );
    biologicalFamilyConditionSisterDiabetesDiedAgee: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionSisterDiabetesDiedAgee',
    );
    biologicalFamilyConditionSisterStrokeAge: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionSisterStrokeAge',
    );
    biologicalFamilyConditionSisterStrokeDied: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionSisterStrokeDied',
    );
    biologicalFamilyConditionSisterStrokeDiedAgee: Parameter = new ApprovalParameter(
        'biologicalFamilyConditionSisterStrokeDiedAgee',
    );
    hasPrimaryCarePhysician: Parameter = new ApprovalParameter('hasPrimaryCarePhysician');
    primaryCarePhysician: Parameter = new ApprovalParameter('primaryCarePhysician');
    primaryCarePhysicianAddress: Parameter = new ApprovalParameter('primaryCarePhysicianAddress');
    primaryCarePhysicianStreet: Parameter = new ApprovalParameter('primaryCarePhysicianStreet');
    primaryCarePhysicianCity: Parameter = new ApprovalParameter('primaryCarePhysicianCity');
    primaryCarePhysicianState: Parameter = new ApprovalParameter('primaryCarePhysicianState');
    primaryCarePhysicianZipCode: Parameter = new ApprovalParameter('primaryCarePhysicianZipCode');
    lastVisit: Parameter = new ApprovalParameter('lastVisit');
    visitReason: Parameter = new ApprovalParameter('visitReason');
    visitOutcome: Parameter = new ApprovalParameter('visitOutcome');
    primaryCarePhysicianPhone: Parameter = new ApprovalParameter('primaryCarePhysicianPhone');
    hasRegularDoctor: Parameter = new ApprovalParameter('hasRegularDoctor');
    lastDoctorVisitProviderName: Parameter = new ApprovalParameter('lastDoctorVisitProviderName');
    lastDoctorVisitStreet: Parameter = new ApprovalParameter('lastDoctorVisitStreet');
    lastDoctorVisitCity: Parameter = new ApprovalParameter('lastDoctorVisitCity');
    lastDoctorVisitState: Parameter = new ApprovalParameter('lastDoctorVisitState');
    lastDoctorVisitZip: Parameter = new ApprovalParameter('lastDoctorVisitZip');
    lastDoctorVisitLastVisited: Parameter = new ApprovalParameter('lastDoctorVisitLastVisited');
    lastDoctorVisitReason: Parameter = new ApprovalParameter('lastDoctorVisitReason');
    lastDoctorVisitReasonAddnTests: Parameter = new ApprovalParameter('lastDoctorVisitReasonAddnTests');
    lastDoctorVisitReasonAddnTestsDetails: Parameter = new ApprovalParameter('lastDoctorVisitReasonAddnTestsDetails');
    lastDoctorVisitOutcome: Parameter = new ApprovalParameter('lastDoctorVisitOutcome');
    lastDoctorVisitPhone: Parameter = new ApprovalParameter('lastDoctorVisitPhone');
    specialistVisits: Parameter = new ApprovalParameter('specialistVisits');
    specialistName: Parameter = new ApprovalParameter('specialistName');
    medicalHistoryPermission: Parameter = new ApprovalParameter('medicalHistoryPermission');
    submissionPermission: Parameter = new ApprovalParameter('submissionPermission');
    acknowlegment: Parameter = new ApprovalParameter('acknowlegment');
    socialSecurityNumber: Parameter = new ApprovalParameter('socialSecurityNumber');
    familyContactPhoneNumber: Parameter = new ApprovalParameter('familyContactPhoneNumber');
    familyContactEmail: Parameter = new ApprovalParameter('familyContactEmail');
    familyContact: Parameter = new ApprovalParameter('familyContact');
    firstCarrierQuoted: Parameter = new ApprovalParameter('firstCarrierQuoted');
    firstProductType: Parameter = new ApprovalParameter('firstProductType');
    firstCoverageAmount: Parameter = new ApprovalParameter('firstCoverageAmount');
    firstMonthlyPremium: Parameter = new ApprovalParameter('firstMonthlyPremium');
    firstHealthRating: Parameter = new ApprovalParameter('firstHealthRating');
    firstFreeResponse: Parameter = new ApprovalParameter('firstFreeResponse');
    preapprovalPivotReason: Parameter = new ApprovalParameter('preapprovalPivotReason');
    preapprovalPivotOtherReason: Parameter = new ApprovalParameter('preapprovalPivotOtherReason');
    recommendedProductCode: Parameter = new ApprovalParameter('recommendedProductCode');
    recommendedFallbackProductCode: Parameter = new ApprovalParameter('recommendedFallbackProductCode');
    selectedProductCode: Parameter = new ApprovalParameter('selectedProductCode');
    agentProductCodes: Parameter = new ApprovalParameter('agentProductCodes'); // New field for actual API product codes
    firstCarrierQuotedCode: Parameter = new ApprovalParameter('firstCarrierQuotedCode'); // Product code for first carrier
    secondCarrierQuoted: Parameter = new ApprovalParameter('secondCarrierQuoted');
    secondCarrierQuotedCode: Parameter = new ApprovalParameter('secondCarrierQuotedCode'); // Product code for second carrier
    secondProductType: Parameter = new ApprovalParameter('secondProductType');
    secondCoverageAmount: Parameter = new ApprovalParameter('secondCoverageAmount');
    secondMonthlyPremium: Parameter = new ApprovalParameter('secondMonthlyPremium');
    secondHealthRating: Parameter = new ApprovalParameter('secondHealthRating');
    secondFreeResponse: Parameter = new ApprovalParameter('secondFreeResponse');
    reapplyCarrierQuoted: Parameter = new ApprovalParameter('reapplyCarrierQuoted');
    reapplyCarrierQuotedCode: Parameter = new ApprovalParameter('reapplyCarrierQuotedCode'); // Product code for reapply carrier
    reapplyProductType: Parameter = new ApprovalParameter('reapplyProductType');
    reapplyCoverageAmount: Parameter = new ApprovalParameter('reapplyCoverageAmount');
    reapplyMonthlyPremium: Parameter = new ApprovalParameter('reapplyMonthlyPremium');
    reapplyHealthRating: Parameter = new ApprovalParameter('reapplyHealthRating');
    nyIllustrationSigned: Parameter = new ApprovalParameter('nyIllustrationSigned');
    conditionalBankName: Parameter = new ApprovalParameter('conditionalBankName');
    conditionalAccountType: Parameter = new ApprovalParameter('conditionalAccountType');
    conditionalAccountHolder: Parameter = new ApprovalParameter('conditionalAccountHolder');
    conditionalRoutingNumber: Parameter = new ApprovalParameter('conditionalRoutingNumber');
    secondaryRoutingNumber: Parameter = new ApprovalParameter('secondaryRoutingNumber');
    conditionalNextAvailable: Parameter = new ApprovalParameter('conditionalNextAvailable');
    conditionalAccountNumber: Parameter = new ApprovalParameter('conditionalAccountNumber');
    secondaryAccountNumber: Parameter = new ApprovalParameter('secondaryAccountNumber');
    conditionalDraftDate: Parameter = new ApprovalParameter('conditionalDraftDate');
    // Bank validation metadata
    primaryAccountValidationDate: Parameter = new ApprovalParameter('primaryAccountValidationDate');
    primaryAccountValidationAgent: Parameter = new ApprovalParameter('primaryAccountValidationAgent');
    primaryAccountValidationError: Parameter = new ApprovalParameter('primaryAccountValidationError');
    secondaryAccountValidationDate: Parameter = new ApprovalParameter('secondaryAccountValidationDate');
    secondaryAccountValidationAgent: Parameter = new ApprovalParameter('secondaryAccountValidationAgent');
    secondaryAccountValidationError: Parameter = new ApprovalParameter('secondaryAccountValidationError');
    conditionalPaymentFreq: Parameter = new ApprovalParameter('conditionalPaymentFreq', 'Monthly');
    conditionalCoverageElected: Parameter = new ApprovalParameter('conditionalCoverageElected');
    notes: Parameter = new ApprovalParameter('notes');
    travelingOut: Parameter = new ApprovalParameter('travelingOut');
    travelingPlace: Parameter = new ApprovalParameter('travelingPlace');
    travelDuration: Parameter = new ApprovalParameter('travelDuration');
    travelReason: Parameter = new ApprovalParameter('travelReason');
    travelDates: Parameter = new ApprovalParameter('travelDates');
    emergencyFirstName: Parameter = new ApprovalParameter('emergencyFirstName');
    emergencyLastName: Parameter = new ApprovalParameter('emergencyLastName');
    emergencyEmail: Parameter = new ApprovalParameter('emergencyEmail');
    emergencyPhone: Parameter = new ApprovalParameter('emergencyPhone');
    emergencyStreetAddr: Parameter = new ApprovalParameter('emergencyStreetAddr');
    emergencyCity: Parameter = new ApprovalParameter('emergencyCity');
    emergencyState: Parameter = new ApprovalParameter('emergencyState');
    emergencyZipCode: Parameter = new ApprovalParameter('emergencyZipCode');
    primaryFundingSource: Parameter = new ApprovalParameter('primaryFundingSource');
    estimatedNetWorth: Parameter = new ApprovalParameter('estimatedNetWorth');
    investedAssets: Parameter = new ApprovalParameter('investedAssets');
    investmentObjective: Parameter = new ApprovalParameter('investmentObjective');
    investmentTimeFrame: Parameter = new ApprovalParameter('investmentTimeFrame');
    investmentRisk: Parameter = new ApprovalParameter('investmentRisk');
    investmentGeneralKnowledge: Parameter = new ApprovalParameter('investmentGeneralKnowledge');
    effectiveTaxRate: Parameter = new ApprovalParameter('effectiveTaxRate');
    annualExpenses: Parameter = new ApprovalParameter('annualExpenses');
    emergencyFundAmount: Parameter = new ApprovalParameter('emergencyFundAmount');
    equitableCDS: Parameter = new ApprovalParameter('equitableCDS');
    equitableStocks: Parameter = new ApprovalParameter('equitableStocks');
    equitableBonds: Parameter = new ApprovalParameter('equitableBonds');
    equitableAnnuities: Parameter = new ApprovalParameter('equitableAnnuities');
    equitableMutualFunds: Parameter = new ApprovalParameter('equitableMutualFunds');
    equitableOtherProperties: Parameter = new ApprovalParameter('equitableOtherProperties');
    equitableRetirementAccounts: Parameter = new ApprovalParameter('equitableRetirementAccounts');
    equitableOtherValue: Parameter = new ApprovalParameter('equitableOtherValue');
    totalAssets: Parameter = new ApprovalParameter('totalAssets');
    equitableInvenstmentExpWithCDs: Parameter = new ApprovalParameter('equitableInvenstmentExpWithCDs');
    equitableInvenstmentExpWithStocks: Parameter = new ApprovalParameter('equitableInvenstmentExpWithStocks');
    equitableInvenstmentExpWithBonds: Parameter = new ApprovalParameter('equitableInvenstmentExpWithBonds');
    equitableInvenstmentExpWithAnnuities: Parameter = new ApprovalParameter('equitableInvenstmentExpWithAnnuities');
    equitableInvenstmentExpWithMutualFunds: Parameter = new ApprovalParameter('equitableInvenstmentExpWithMutualFunds');
    equitableInvestmentPurposeRecommendation: Parameter = new ApprovalParameter(
        'equitableInvestmentPurposeRecommendation',
    );
    equitableInvestmentRisk: Parameter = new ApprovalParameter('equitableInvestmentRisk');
    equitableEffectiveTaxRate: Parameter = new ApprovalParameter('equitableEffectiveTaxRate');
    equitableConcerns: Parameter = new ApprovalParameter('equitableConcerns');
    isPrincipalProtectionOrIncomeGuarantees: Parameter = new ApprovalParameter(
        'isPrincipalProtectionOrIncomeGuarantees',
    );
    interestedInAdditionalPolicy: Parameter = new ApprovalParameter('interestedInAdditionalPolicy');
    upsellMaritalStatus: Parameter = new ApprovalParameter('upsellMaritalStatus');
    kidsNumber: Parameter = new ApprovalParameter('kidsNumber');
    decisionLater1HrMark: Parameter = new ApprovalParameter('decisionLater1HrMark');
    fgAnyInsuranceDeclinedPostponedRescinded: Parameter = new ApprovalParameter(
        'fgAnyInsuranceDeclinedPostponedRescinded',
    );
    fgAnyInsuranceDeclinedPostponedRescindedDetail: Parameter = new ApprovalParameter(
        'fgAnyInsuranceDeclinedPostponedRescindedDetail',
    );
    fgTraveledResidedOutsideUSOrCanadaPast2YrOrPlanInNext2Yr: Parameter = new ApprovalParameter(
        'fgTraveledResidedOutsideUSOrCanadaPast2YrOrPlanInNext2Yr',
    );
    fgPleadedGuiltyBeenConvictedFelony: Parameter = new ApprovalParameter('fgPleadedGuiltyBeenConvictedFelony');
    fgMarijuanaPast5Years: Parameter = new ApprovalParameter('fgMarijuanaPast5Years');
    fgHeroinCocaineNarcoticsBarbituratesAmphetaminesHallucinogensControlledSubstance: Parameter = new ApprovalParameter(
        'fgHeroinCocaineNarcoticsBarbituratesAmphetaminesHallucinogensControlledSubstance',
    );
    fgHeroinCocaineNarcoticsBarbituratesAmphetaminesHallucinogensControlledSubstanceDetail: Parameter =
        new ApprovalParameter('fgHeroinCocaineNarcoticsBarbituratesAmphetaminesHallucinogensControlledSubstanceDetail');
    fgAlcoholDrugTreatment: Parameter = new ApprovalParameter('fgAlcoholDrugTreatment');
    fgDUIDWIPast5yrs: Parameter = new ApprovalParameter('fgDUIDWIPast5yrs');
    fgMovingViolationsPast5Years: Parameter = new ApprovalParameter('fgMovingViolationsPast5Years');
    fgBankruptcyPast7Years: Parameter = new ApprovalParameter('fgBankruptcyPast7Years');
    fgParticipateInRacingMountainClimbingScubaDivingBungeeJumpingHangGlidingSkydivingAerialSportsPast5YrsOrNext2Yrs: Parameter =
        new ApprovalParameter(
            'fgParticipateInRacingMountainClimbingScubaDivingBungeeJumpingHangGlidingSkydivingAerialSportsPast5YrsOrNext2Yrs',
        );
    fgParticipateInRacingMountainClimbingScubaDivingBungeeJumpingHangGlidingSkydivingAerialSportsPast5YrsOrNext2YrsDetail: Parameter =
        new ApprovalParameter(
            'fgParticipateInRacingMountainClimbingScubaDivingBungeeJumpingHangGlidingSkydivingAerialSportsPast5YrsOrNext2YrsDetail',
        );
    fgPilotStudentPilotCrewMemberPast5YearsOrNext2Years: Parameter = new ApprovalParameter(
        'fgPilotStudentPilotCrewMemberPast5YearsOrNext2Years',
    );
    fgFinancingMortgageHomeEquityLoanPremiums: Parameter = new ApprovalParameter(
        'fgFinancingMortgageHomeEquityLoanPremiums',
    );
    fgBorrowMoneyPremium: Parameter = new ApprovalParameter('fgBorrowMoneyPremium');
    fgLifeInsuranceAnnuitySoldAssignedToLifeSettlementViaticalSecondaryMarketProvider: Parameter =
        new ApprovalParameter('fgLifeInsuranceAnnuitySoldAssignedToLifeSettlementViaticalSecondaryMarketProvider');
    fgLifeInsuranceAnnuitySoldAssignedToLifeSettlementViaticalSecondaryMarketProviderDetail: Parameter =
        new ApprovalParameter(
            'fgLifeInsuranceAnnuitySoldAssignedToLifeSettlementViaticalSecondaryMarketProviderDetail',
        );
    fgTraveledResidedOutsideUSOrCanadaPast2YrOrPlanInNext2YrDetail: Parameter = new ApprovalParameter(
        'fgTraveledResidedOutsideUSOrCanadaPast2YrOrPlanInNext2YrDetail',
    );
    fgPleadedGuiltyBeenConvictedFelonyDetail: Parameter = new ApprovalParameter(
        'fgPleadedGuiltyBeenConvictedFelonyDetail',
    );
    fgAlcoholDrugTreatmentDetail: Parameter = new ApprovalParameter('fgAlcoholDrugTreatmentDetail');
    fgDUIDWIPast5yrsDetail: Parameter = new ApprovalParameter('fgDUIDWIPast5yrsDetail');
    fgMovingViolationsPast5YearsDetail: Parameter = new ApprovalParameter('fgMovingViolationsPast5YearsDetail');
    fgBankruptcyPast7YearsDetail: Parameter = new ApprovalParameter('fgBankruptcyPast7YearsDetail');
    fgPilotStudentPilotCrewMemberPast5YearsOrNext2YearsDetail: Parameter = new ApprovalParameter(
        'fgPilotStudentPilotCrewMemberPast5YearsOrNext2YearsDetail',
    );
    fgFinancingMortgageHomeEquityLoanPremiumsDetail: Parameter = new ApprovalParameter(
        'fgFinancingMortgageHomeEquityLoanPremiumsDetail',
    );
    fgBorrowMoneyPremiumDetail: Parameter = new ApprovalParameter('fgBorrowMoneyPremiumDetail');
    fgBeenAdvicedDiagnosticTestTreatmentSurgeryOrOtherProcedureWhichNotBeenPerformed: Parameter = new ApprovalParameter(
        'fgBeenAdvicedDiagnosticTestTreatmentSurgeryOrOtherProcedureWhichNotBeenPerformed',
    );
    fgBeenAdvicedDiagnosticTestTreatmentSurgeryOrOtherProcedureWhichNotBeenPerformedDetail: Parameter =
        new ApprovalParameter('fgBeenAdvicedDiagnosticTestTreatmentSurgeryOrOtherProcedureWhichNotBeenPerformedDetail');
    fgBeenTreatedDiagnosedMenthalPhysicalDisorder: Parameter = new ApprovalParameter(
        'fgBeenTreatedDiagnosedMenthalPhysicalDisorder',
    );
    fgBeenTreatedDiagnosedMenthalPhysicalDisorderDetail: Parameter = new ApprovalParameter(
        'fgBeenTreatedDiagnosedMenthalPhysicalDisorderDetail',
    );
    fgHadEKGXRayLabTestTreatmentProcedure: Parameter = new ApprovalParameter('fgHadEKGXRayLabTestTreatmentProcedure');
    fgHadEKGXRayLabTestTreatmentProcedureDetail: Parameter = new ApprovalParameter(
        'fgHadEKGXRayLabTestTreatmentProcedureDetail',
    );
    fgBeenHospitilezedOrHadSurgeryOrProcedure: Parameter = new ApprovalParameter(
        'fgBeenHospitilezedOrHadSurgeryOrProcedure',
    );
    fgBeenHospitilezedOrHadSurgeryOrProcedureDetail: Parameter = new ApprovalParameter(
        'fgBeenHospitilezedOrHadSurgeryOrProcedureDetail',
    );
    fgHeartBloodVesselsDisorder: Parameter = new ApprovalParameter('fgHeartBloodVesselsDisorder');
    fgHeartBloodVesselsDisorderDetail: Parameter = new ApprovalParameter('fgHeartBloodVesselsDisorderDetail');
    fgCirculatorySystemDisorder: Parameter = new ApprovalParameter('fgCirculatorySystemDisorder');
    fgCirculatorySystemDisorderDetail: Parameter = new ApprovalParameter('fgCirculatorySystemDisorderDetail');
    fgLungsOrRespiratorySystemDisorder: Parameter = new ApprovalParameter('fgLungsOrRespiratorySystemDisorder');
    fgLungsOrRespiratorySystemDisorderDetail: Parameter = new ApprovalParameter(
        'fgLungsOrRespiratorySystemDisorderDetail',
    );
    fgImmuneSystemOrEndocrineSystemDisorder: Parameter = new ApprovalParameter(
        'fgImmuneSystemOrEndocrineSystemDisorder',
    );
    fgImmuneSystemOrEndocrineSystemDisorderDetail: Parameter = new ApprovalParameter(
        'fgImmuneSystemOrEndocrineSystemDisorderDetail',
    );
    fgCancerTumorsPolypsCysts: Parameter = new ApprovalParameter('fgCancerTumorsPolypsCysts');
    fgCancerTumorsPolypsCystsDetail: Parameter = new ApprovalParameter('fgCancerTumorsPolypsCystsDetail');
    fgPsychiatricOrMentalDisorder: Parameter = new ApprovalParameter('fgPsychiatricOrMentalDisorder');
    fgPsychiatricOrMentalDisorderDetail: Parameter = new ApprovalParameter('fgPsychiatricOrMentalDisorderDetail');
    fgLupusOrOtherConnectiveTissueDiseaseAutoimmuneDisorder: Parameter = new ApprovalParameter(
        'fgLupusOrOtherConnectiveTissueDiseaseAutoimmuneDisorder',
    );
    fgLupusOrOtherConnectiveTissueDiseaseAutoimmuneDisorderDetail: Parameter = new ApprovalParameter(
        'fgLupusOrOtherConnectiveTissueDiseaseAutoimmuneDisorderDetail',
    );
    fgStomachLiverIntestinesColonPancreasDiseaseDisorder: Parameter = new ApprovalParameter(
        'fgStomachLiverIntestinesColonPancreasDiseaseDisorder',
    );
    fgStomachLiverIntestinesColonPancreasDiseaseDisorderDetail: Parameter = new ApprovalParameter(
        'fgStomachLiverIntestinesColonPancreasDiseaseDisorderDetail',
    );
    fgKidneysBladderUrinarySystemProstateBreastsReproductiveSystemDiseaseDisorder: Parameter = new ApprovalParameter(
        'fgKidneysBladderUrinarySystemProstateBreastsReproductiveSystemDiseaseDisorder',
    );
    fgKidneysBladderUrinarySystemProstateBreastsReproductiveSystemDiseaseDisorderDetail: Parameter =
        new ApprovalParameter('fgKidneysBladderUrinarySystemProstateBreastsReproductiveSystemDiseaseDisorderDetail');
    fgMuscleBonesSpineJointsDiseaseDisorder: Parameter = new ApprovalParameter(
        'fgMuscleBonesSpineJointsDiseaseDisorder',
    );
    fgMuscleBonesSpineJointsDiseaseDisorderDetail: Parameter = new ApprovalParameter(
        'fgMuscleBonesSpineJointsDiseaseDisorderDetail',
    );
    fgSkinEyesEarsNoseThroatDiseaseDisorder: Parameter = new ApprovalParameter(
        'fgSkinEyesEarsNoseThroatDiseaseDisorder',
    );
    fgSkinEyesEarsNoseThroatDiseaseDisorderDetail: Parameter = new ApprovalParameter(
        'fgSkinEyesEarsNoseThroatDiseaseDisorderDetail',
    );

    get calculatedContNumberOfBenes() {
        const param: Parameter = new ApprovalParameter('calculatedContNumberOfBenes');
        param.value = this.contBene.value ? Number(this.contNumberOfBenes) : 0;
        return param;
    }

    constructor() {
        makeAutoObservable(this);
    }

    static isRequiredField(
        key: string,
        coverages?: string[],
        existingLifeInsurancePolicies?: boolean,
        primaryCarePhysician?: boolean,
        fgMarijuanaPast5Years?: boolean,
        prosperNotChosenReason?: string,
        shouldCheckBeneficiaryAdditionalValue?: boolean,
        shouldCheckConditionalDraftDate?: boolean,
    ) {
        const shouldIncludeProsperReason = !!coverages?.length && !coverages.includes('Prosper');
        const shouldIncludeFGEverlastReason =
            !!coverages?.length && !coverages.includes('Prosper') && !coverages.includes('FGEverlast');

        const shouldIncludeProsperDetails =
            shouldIncludeProsperReason &&
            prosperNotChosenReason &&
            [
                'OtherReason',
                'OtherOtherReason',
                'EligibilityOtherReason',
                'ClientRequestedOtherReason',
                'CapabilityQuotingTechIssue',
                'CapabilityOtherReason',
            ].includes(prosperNotChosenReason);
        return [
            ...(shouldIncludeProsperReason ? ['prosperNotChosenReason'] : []),
            ...(shouldIncludeFGEverlastReason ? ['fgEverlastNotChosenReason'] : []),
            ...(shouldIncludeProsperDetails ? ['prosperNotChosenDetails'] : []),
            ...(shouldCheckBeneficiaryAdditionalValue
                ? [
                      'beneType1',
                      'beneRelationshipAgenium1',
                      'beneType2',
                      'beneRelationshipAgenium2',
                      'beneType3',
                      'beneRelationshipAgenium3',
                      'beneType4',
                      'beneRelationshipAgenium4',
                      'beneType5',
                      'beneRelationshipAgenium5',
                      'beneType6',
                      'beneRelationshipAgenium6',
                      'contBeneType1',
                      'contBeneRelationshipAgenium1',
                      'contBeneType2',
                      'contBeneRelationshipAgenium2',
                      'contBeneType3',
                      'contBeneRelationshipAgenium3',
                      'contBeneType4',
                      'contBeneRelationshipAgenium4',
                      'contBeneType5',
                      'contBeneRelationshipAgenium5',
                      'contBeneType6',
                      'contBeneRelationshipAgenium6',
                  ]
                : []),
            ...(shouldCheckConditionalDraftDate ? ['conditionalDraftDate'] : []),
            'firstName',
            'lastName',
            'phoneNumber',
            'emailAddress',
            'bmonth',
            'bday',
            'byear',
            'age',
            'gender',
            'maritalStatus',
            'address',
            'addressCity',
            'state',
            'addressZipCode',
            'height',
            'weight',
            'weightChanged',
            'sameOwner',
            'policyOwnerName',
            'ownerSocialSecurityNumber',
            'ownerBMonth',
            'ownerBday',
            'ownerByear',
            'ownerIssueState',
            'ownerNumber',
            'ownerCitizenshipCountry',
            'relationshipToInsured',
            'policyOwnerPhone',
            'policyOwnerEmail',
            'policyOwnerStreetAddress',
            'policyOwnerState',
            'policyOwnerCity',
            'policyOwnerZipcode',
            'incomeIndividual',
            'incomeHouseholdText',
            'currentlyEmployed',
            'currentSelfEmployed',
            'nameOfEmployer',
            'timeEmployed',
            'employerAddress',
            'occupation',
            'industryDuties',
            'prevSelfEmployed',
            'prevEmployName',
            'prevEmployStart',
            'prevEmployOccup',
            'prevEmployIndustry',
            'reasonUnemployed',
            'previousJobInfo',
            'previousIncome',
            'retiredInfo',
            'retiredIncome',
            'schoolInfo',
            'disabilityInfo',
            'otherInfo',
            'birthCountry',
            'birthState',
            'usCitizenship',
            'usPermResNumber',
            'usVisaType',
            'usVisaNumber',
            'usTinNumber',
            'validDriversLicense',
            'driversLicenseState',
            'driversLicenseNumber',
            'driversLicExpiration',
            'stateIdState',
            'stateIdNumber',
            'stateIdExpiration',
            'noDriversLicenseReason',
            'modeOfTransport',
            'military',
            'numberOfBenes',
            'beneInfo1',
            'benePercent1',
            'beneRelationship1',
            'beneMonth1',
            'beneDay1',
            'beneYear1',
            'beneInfo2',
            'benePercent2',
            'beneRelationship2',
            'beneMonth2',
            'beneDay2',
            'beneYear2',
            'beneInfo3',
            'benePercent3',
            'beneRelationship3',
            'beneMonth3',
            'beneDay3',
            'beneYear3',
            'beneInfo4',
            'benePercent4',
            'beneRelationship4',
            'beneMonth4',
            'beneDay4',
            'beneYear4',
            'beneInfo5',
            'benePercent5',
            'beneRelationship5',
            'beneMonth5',
            'beneDay5',
            'beneYear5',
            'beneInfo6',
            'benePercent6',
            'beneRelationship6',
            'beneMonth6',
            'beneDay6',
            'beneYear6',
            'contBene',
            'contNumberOfBenes',
            'contBeneInfo1',
            'contBenePercent1',
            'contBeneRelationship1',
            'contBeneMonth1',
            'contBday1',
            'contBeneYear1',
            'contBeneInfo2',
            'contBenePercent2',
            'contBeneRelationship2',
            'contBeneMonth2',
            'contBday2',
            'contBeneYear2',
            'contBeneInfo3',
            'contBenePercent3',
            'contBeneRelationship3',
            'contBeneMonth3',
            'contBday3',
            'contBeneYear3',
            'contBeneInfo4',
            'contBenePercent4',
            'contBeneRelationship4',
            'contBeneMonth4',
            'contBday4',
            'contBeneYear4',
            'contBeneInfo5',
            'contBenePercent5',
            'contBeneRelationship5',
            'contBeneMonth5',
            'contBday5',
            'contBeneYear5',
            'contBeneInfo6',
            'contBenePercent6',
            'contBeneRelationship6',
            'contBeneMonth6',
            'contBday6',
            'contBeneYear6',
            'existingCoverage',
            'existingCarrier',
            'existingPolicyType',
            'existingCoverageAmount',
            'replacingExistingPolicy',
            'shoppedCoverage',
            'shoppedCarrier',
            'shoppedPolicyType',
            'shoppedCoverageAmount',
            'medicationHistory',
            'HIV',
            'TobaccoRating',
            'tobaccoProducts',
            'tobaccoFrequency',
            'tobaccoLastUse',
            ...(coverages?.includes('FGPathsetterIUL')
                ? [
                      'fgBeenAdvicedDiagnosticTestTreatmentSurgeryOrOtherProcedureWhichNotBeenPerformed',
                      'fgBeenTreatedDiagnosedMenthalPhysicalDisorder',
                      'fgHadEKGXRayLabTestTreatmentProcedure',
                      'fgBeenHospitilezedOrHadSurgeryOrProcedure',
                      'fgHeartBloodVesselsDisorder',
                      'fgCirculatorySystemDisorder',
                      'fgLungsOrRespiratorySystemDisorder',
                      'fgImmuneSystemOrEndocrineSystemDisorder',
                      'fgCancerTumorsPolypsCysts',
                      'fgPsychiatricOrMentalDisorder',
                      'fgLupusOrOtherConnectiveTissueDiseaseAutoimmuneDisorder',
                      'fgStomachLiverIntestinesColonPancreasDiseaseDisorder',
                      'fgKidneysBladderUrinarySystemProstateBreastsReproductiveSystemDiseaseDisorder',
                      'fgMuscleBonesSpineJointsDiseaseDisorder',
                      'fgSkinEyesEarsNoseThroatDiseaseDisorder',
                      'fgAnyInsuranceDeclinedPostponedRescinded',
                      'fgTraveledResidedOutsideUSOrCanadaPast2YrOrPlanInNext2Yr',
                      'fgPleadedGuiltyBeenConvictedFelony',
                      'fgMarijuanaPast5Years',
                      ...(fgMarijuanaPast5Years ? ['marijuanaProducts', 'marijuanaFrequency'] : []),
                      'fgHeroinCocaineNarcoticsBarbituratesAmphetaminesHallucinogensControlledSubstance',
                      'fgAlcoholDrugTreatment',
                      'fgDUIDWIPast5yrs',
                      'fgMovingViolationsPast5Years',
                      'fgBankruptcyPast7Years',
                      'fgParticipateInRacingMountainClimbingScubaDivingBungeeJumpingHangGlidingSkydivingAerialSportsPast5YrsOrNext2Yrs',
                      'fgPilotStudentPilotCrewMemberPast5YearsOrNext2Years',
                      'fgFinancingMortgageHomeEquityLoanPremiums',
                      'fgBorrowMoneyPremium',
                      'fgLifeInsuranceAnnuitySoldAssignedToLifeSettlementViaticalSecondaryMarketProvider',
                      ...(existingLifeInsurancePolicies
                          ? ['policyNumber', 'accidentalDeathBenefitAmount', 'howLongInforce']
                          : []),
                  ]
                : []),
            ...(primaryCarePhysician === false
                ? []
                : [
                      'primaryCarePhysician',
                      'primaryCarePhysicianAddress',
                      'primaryCarePhysicianStreet',
                      'primaryCarePhysicianCity',
                      'primaryCarePhysicianState',
                      'primaryCarePhysicianZipCode',
                      ...(coverages?.includes('FGPathsetterIUL') ? ['lastVisit'] : []),
                      'visitReason',
                      'visitOutcome',
                      'primaryCarePhysicianPhone',
                  ]),
            'medicalHistoryPermission',
            'submissionPermission',
            'acknowlegment',
            'socialSecurityNumber',
            'firstCarrierQuoted',
            'firstProductType',
            'firstCoverageAmount',
            'firstMonthlyPremium',
            'firstHealthRating',
            'nyIllustrationSigned',
            'travelingOut',
            'travelingPlace',
            'travelDuration',
            'travelReason',
            'travelDates',
            ...(coverages?.length === 0 ||
            coverages?.includes('EquitableVUL') ||
            coverages?.includes('LSWIUL') ||
            coverages?.includes('Prosper')
                ? [
                      'additionalConditions',
                      'MarijuanaRating',
                      'OtherRating',
                      'MarijuanaRating',
                      'marijuanaProducts',
                      'marijuanaFrequency',
                      'marijuanaLastUse',
                      'otherSubstance',
                      'otherProducts',
                      'otherFrequency',
                      'otherLastUse',
                      'DrugorAlcoholDependenceWithdrawal',
                      'DUI',
                      'License',
                      'Movingviolations',
                      'Criminaloffense',
                      'Bankruptcy',
                      'extremeSports',
                      'extremeSportsOptions',
                      'extremeSportsScubaType',
                      'extremeSportsScubaDepth',
                      'livingAssistance',
                  ]
                : []),
            'fatherLiving',
            'fatherAge',
            'fatherPassingAge',
            'fatherPassingCause',
            'fatherUnknown',
            'motherLiving',
            'motherAge',
            'motherPassingAge',
            'motherPassingCause',
            'motherUnknown',
            'biologicalFamilyCondition',
            'biologicalFamilyConditionWhichMember',
            'biologicalFamilyConditionFather',
            'biologicalFamilyConditionFatherCancerDiagnosedAge',
            'biologicalFamilyConditionFatherCancerDied',
            'biologicalFamilyConditionFatherCancerDiedAge',
            'biologicalFamilyConditionFatherCancerType',
            'biologicalFamilyConditionFatherHeartDiseaseAge',
            'biologicalFamilyConditionFatherHeartDiseaseDied',
            'biologicalFamilyConditionFatherHeartDiseaseDiedAgee',
            'biologicalFamilyConditionFatherDiabetesAge',
            'biologicalFamilyConditionFatherDiabetesDied',
            'biologicalFamilyConditionFatherDiabetesDiedAgee',
            'biologicalFamilyConditionFatherStrokeAge',
            'biologicalFamilyConditionFatherStrokeDied',
            'biologicalFamilyConditionFatherStrokeDiedAgee',
            'biologicalFamilyConditionMother',
            'biologicalFamilyConditionMotherCancerDiagnosedAge',
            'biologicalFamilyConditionMotherCancerDied',
            'biologicalFamilyConditionMotherCancerDiedAge',
            'biologicalFamilyConditionMotherCancerType',
            'biologicalFamilyConditionMotherHeartDiseaseAge',
            'biologicalFamilyConditionMotherHeartDiseaseDied',
            'biologicalFamilyConditionMotherHeartDiseaseDiedAgee',
            'biologicalFamilyConditionMotherDiabetesAge',
            'biologicalFamilyConditionMotherDiabetesDied',
            'biologicalFamilyConditionMotherDiabetesDiedAgee',
            'biologicalFamilyConditionMotherStrokeAge',
            'biologicalFamilyConditionMotherStrokeDied',
            'biologicalFamilyConditionMotherStrokeDiedAgee',
            'biologicalFamilyConditionBrother',
            'biologicalFamilyConditionBrotherCancerDiagnosedAge',
            'biologicalFamilyConditionBrotherCancerDied',
            'biologicalFamilyConditionBrotherCancerDiedAge',
            'biologicalFamilyConditionBrotherCancerType',
            'biologicalFamilyConditionBrotherHeartDiseaseAge',
            'biologicalFamilyConditionBrotherHeartDiseaseDied',
            'biologicalFamilyConditionBrotherHeartDiseaseDiedAgee',
            'biologicalFamilyConditionBrotherDiabetesAge',
            'biologicalFamilyConditionBrotherDiabetesDied',
            'biologicalFamilyConditionBrotherDiabetesDiedAgee',
            'biologicalFamilyConditionBrotherStrokeAge',
            'biologicalFamilyConditionBrotherStrokeDied',
            'biologicalFamilyConditionBrotherStrokeDiedAgee',
            'biologicalFamilyConditionSister',
            'biologicalFamilyConditionSisterCancerDiagnosedAge',
            'biologicalFamilyConditionSisterCancerDied',
            'biologicalFamilyConditionSisterCancerDiedAge',
            'biologicalFamilyConditionSisterCancerType',
            'biologicalFamilyConditionSisterHeartDiseaseAge',
            'biologicalFamilyConditionSisterHeartDiseaseDied',
            'biologicalFamilyConditionSisterHeartDiseaseDiedAgee',
            'biologicalFamilyConditionSisterDiabetesAge',
            'biologicalFamilyConditionSisterDiabetesDied',
            'biologicalFamilyConditionSisterDiabetesDiedAgee',
            'biologicalFamilyConditionSisterStrokeAge',
            'biologicalFamilyConditionSisterStrokeDied',
            'biologicalFamilyConditionSisterStrokeDiedAgee',
        ].includes(key);
    }
}
